<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-usd-circle"></i> Currency
      </Tab>
      <Tab tabid="tab-2">
        <i class="far fa-donate"></i> Tax Rates
      </Tab>
      <Tab tabid="tab-3">
        <i class="far fa-caret-square-down"></i> Lookup Lists
      </Tab>
      <Tab tabid="tab-4">
        <i class="far fa-list-ol"></i> Sequence Numbers
      </Tab>
      <Tab tabid="tab-5">
        <i class="far fa-comment-lines"></i> Message Templates
      </Tab>
      <Tab tabid="tab-6">
        <i class="far fa-print"></i> Report Config
      </Tab>
      <Tab tabid="tab-7">
        <i class="far fa-tablet"></i> Kiosk Config
      </Tab>
      <Tab tabid="tab-8">
        <i class="far fa-barcode-alt"></i> Print Label Config
      </Tab>
      <Tab tabid="tab-9">
        <i class="far fa-stopwatch"></i> Shifts Config
      </Tab>
      <Tab tabid="tab-10">
        <i class="far fa-calendar"></i> Calendar Config
      </Tab>
      <Tab tabid="tab-11">
        <i class="far fa-tag"></i> Label Type List
      </Tab>
    </template>
    <template v-slot:tabcontent>

      <!-- Currency Tab -->
      <TabPage :first="true" tabid="tab-1">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.configuration)" class="float-right">
              <DropdownMenuItem @click="newCurrency" icon="fa-plus">Add Currency</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Currency List</h3>
          </div>
        </div>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Base</th>
              <th>Target</th>
              <th>Market Rate</th>
              <th>Account Rate</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="currency in currencyList" :key="currency.id">
              <td>{{accountCurrencyCode}}</td>
              <td>{{currency.targetCode}}</td>
              <td>{{currency.marketRate | formatCurrency(3)}} {{accountCurrencyCode}}</td>
              <td>
                <span v-if="currency.useMarketRate">Using Market Rate</span>
                <span v-if="!currency.useMarketRate">{{currency.rate | formatCurrency(3)}} {{accountCurrencyCode}}</span>
              </td>
              <td>
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) || userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" :tableAction="true">
                  <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editCurrency(currency)" icon="fa-pencil">Edit Currency</DropdownMenuItem>
                  <DropdownMenuItem v-if="userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="deleteCurrency(currency)" icon="fa-trash-alt">Delete Currency</DropdownMenuItem>
                </ActionDropMenu>                  
              </td>
            </tr>
          </template>
        </StaticDataTable>
        <p class="text-muted"><small>Note: Market rates are updated daily and may not be up to date</small></p>
      </TabPage>

      <!-- Tax Rates Tab -->
      <TabPage tabid="tab-2">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.configuration)" class="float-right">
              <DropdownMenuItem @click="newTaxRate" icon="fa-plus">Add Tax Rate</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Tax Rates List</h3>
          </div>
        </div>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Rate</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="taxRate in taxRateList" :key="taxRate.id">
              <td>{{taxRate.name}}</td>
              <td>{{taxRate.code}}</td>
              <td>{{taxRate.rate | formatPercent}}</td>
              <td>
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) || userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" :tableAction="true">
                  <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editTaxRate(taxRate)" icon="fa-pencil">Edit Tax Rate</DropdownMenuItem>
                  <DropdownMenuItem v-if="userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="deleteTaxRate(taxRate)" icon="fa-trash-alt">Delete Tax Rate</DropdownMenuItem>
                </ActionDropMenu>                  
              </td>
            </tr>
          </template>
        </StaticDataTable>
      </TabPage>

      <!-- Lookup List Tab -->
      <TabPage tabid="tab-3">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.configuration)" class="float-right">
              <DropdownMenuItem @click="newDescriptorLookup" icon="fa-plus">Add Lookup</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Lookup Lists</h3>
          </div>
        </div>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>Lookup Value</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="lookup in descriptorList" :key="lookup.id">
              <td>{{getLookupName(descriptorLookupOptions, lookup.type)}}</td>
              <td>{{lookup.description}}</td>
              <td>{{formatHyphenEmptyValue(lookup.value)}}</td>
              <td>
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) || userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" :tableAction="true">
                  <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editDescriptorLookup(lookup)" icon="fa-pencil">Edit Lookup</DropdownMenuItem>
                  <DropdownMenuItem v-if="userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="deleteDescriptorLookup(lookup)" icon="fa-trash-alt">Delete Lookup</DropdownMenuItem>
                </ActionDropMenu>                  
              </td>
            </tr>
          </template>
        </StaticDataTable>
      </TabPage>

      <!-- Sequence Numbers -->
      <TabPage tabid="tab-4">
        <div class="row">
          <div class="col-lg-12">
            <h3>Sequence Numbers</h3>
          </div>
        </div>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Next Sequence</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="sequence in sequenceList" :key="sequence.id">
              <td>{{sequence.name}}</td>
              <td>{{sequence.prefix}}</td>
              <td>{{displaySequenceNumber(sequence.prefix, sequence.paddingCount, sequence.nextNumber)}}</td>
              <td>
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" :tableAction="true">
                  <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editSequence(sequence)" icon="fa-pencil">Edit Sequence</DropdownMenuItem>
                </ActionDropMenu>                  
              </td>
            </tr>
          </template>
        </StaticDataTable>
      </TabPage>

      <!-- Message Templates -->
      <TabPage tabid="tab-5">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.configuration)" class="float-right">
              <DropdownMenuItem @click="newMessageTemplate" icon="fa-plus">Add Template</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Message Templates</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mt-2">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Method</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="template in messageTemplateList" :key="template.id">
                  <td>
                    <span class="font-weight-bold">{{template.name}}</span>
                    <span class="label label-success ml-2" v-if="template.isDefault">Default</span>
                  </td>
                  <td>{{getLookupName(messageTemplateLookupOptions, template.templateType)}}</td>
                  <td>{{getLookupName(commMethodLookupOptions, template.methodType)}}</td>              
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) || userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" :tableAction="true">
                      <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editMessageTemplate(template)" icon="fa-pencil">Edit Template</DropdownMenuItem>
                      <DropdownMenuItem v-if="userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="deleteMessageTemplate(template)" icon="fa-trash-alt">Delete Template</DropdownMenuItem>
                    </ActionDropMenu>                  
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <!-- Report Config Tab -->
      <TabPage tabid="tab-6">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) && !reportSettings.editing" class="float-right">
              <DropdownMenuItem @click="reportSettings.editing = true" icon="fa-pencil">Edit Config</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Report Configuration</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mt-2">
            <FormGroup :labelColumnSize="4" :form="reportSettings.form">
              <strong>Accounts</strong>

              <FormRow label="Payment Advice">
                <template v-slot:label>
                  <InlineHelpPopover title="Payment Advice Details" placement="right">
                    <p>Enter the description for the payment advice to be displayed on the various reports</p>
                    <p>Should include payment account details or contact details customers need to make payment</p>
                  </InlineHelpPopover>
                </template>
                <FormTextEditor v-if="reportSettings.editing" v-model="reportSettings.settings.paymentAdviceText" />
                <FormStaticLabel v-if="!reportSettings.editing">
                  <div class="text-editor-static-display" v-html="reportSettings.settings.paymentAdviceText" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Payment Advice - Customer Statement">
                <template v-slot:label>
                  <InlineHelpPopover title="Payment Advice - Customer Statement" placement="right">
                    <p>If enabled, shows the payment advice entered above on the customer statements</p>
                  </InlineHelpPopover>
                </template>
                <FormStaticLabel>
                  <IconBoolean v-if="!reportSettings.editing" :value="reportSettings.settings.paymentAdviceStatementEnabled" />
                  <FormCheckbox v-if="reportSettings.editing" v-model="reportSettings.settings.paymentAdviceStatementEnabled" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Payment Advice - Orders">
                <template v-slot:label>
                  <InlineHelpPopover title="Payment Advice - Orders" placement="right">
                    <p>If enabled, shows the payment advice entered above on order sheets (with pricing)</p>
                  </InlineHelpPopover>
                </template>
                <FormStaticLabel>
                  <IconBoolean v-if="!reportSettings.editing" :value="reportSettings.settings.paymentAdviceOrderEnabled" />
                  <FormCheckbox v-if="reportSettings.editing" v-model="reportSettings.settings.paymentAdviceOrderEnabled" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Payment Advice - Invoices">
                <template v-slot:label>
                  <InlineHelpPopover title="Payment Advice - Invoices" placement="right">
                    <p>If enabled, shows the payment advice entered above on invoices</p>
                  </InlineHelpPopover>
                </template>
                <FormStaticLabel>
                  <IconBoolean v-if="!reportSettings.editing" :value="reportSettings.settings.paymentAdviceInvoiceEnabled" />
                  <FormCheckbox v-if="reportSettings.editing" v-model="reportSettings.settings.paymentAdviceInvoiceEnabled" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Credit Advice">
                <template v-slot:label>
                  <InlineHelpPopover title="Credit Details" placement="right">
                    <p>Enter the description for the credit advice to be displayed on the credit note receipt report</p>
                    <p>Should include details declaring this as credit and not to be paid</p>
                  </InlineHelpPopover>
                </template>
                <FormTextEditor v-if="reportSettings.editing" v-model="reportSettings.settings.creditAdviceText" />
                <FormStaticLabel v-if="!reportSettings.editing">
                  <div class="text-editor-static-display" v-html="reportSettings.settings.creditAdviceText" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Payment Received">
                <template v-slot:label>
                  <InlineHelpPopover title="Payment Received Details" placement="right">
                    <p>Enter the description for the payment received notice to be displayed on the payment received receipt report</p>
                    <p>Can include a thank you / payment received confirmation message</p>
                  </InlineHelpPopover>
                </template>
                <FormTextEditor v-if="reportSettings.editing" v-model="reportSettings.settings.paymentReceivedText" />
                <FormStaticLabel v-if="!reportSettings.editing">
                  <div class="text-editor-static-display" v-html="reportSettings.settings.paymentReceivedText" />
                </FormStaticLabel>
              </FormRow>

              <FormSubmit v-if="reportSettings.editing" label="Update" :submitting="reportSettings.submitting" @submitted="submitReportSettings" @cancelled="cancelReportSettings" />
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <!-- Kiosk Config Tab -->
      <TabPage tabid="tab-7">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) && !kioskSettings.editing" class="float-right">
              <DropdownMenuItem @click="kioskSettings.editing = true" icon="fa-pencil">Edit Config</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Kiosk Configuration</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mt-2">
            <FormGroup :labelColumnSize="4" :form="kioskSettings.form">

              <FormRow label="Reception - View Orders Enabled">
                <template v-slot:label>
                  <InlineHelpPopover title="Reception - View Orders Enabled" placement="right">
                    <p>If enabled, the reception kiosk will show a view orders button on the main reception dashboard screen for customers to use to view their orders</p>
                  </InlineHelpPopover>
                </template>
                <FormCheckbox v-if="kioskSettings.editing" v-model="kioskSettings.settings.receptionViewOrdersEnabled" />
                <FormStaticLabel v-if="!kioskSettings.editing">
                  <IconBoolean :value="kioskSettings.settings.receptionViewOrdersEnabled" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Reception - Dashboard Title">
                <template v-slot:label>
                  <InlineHelpPopover title="Reception - Dashboard Title" placement="right">
                    <p>The title displayed on the main reception dashboard screen. Typically a short welcome message</p>
                  </InlineHelpPopover>
                </template>
                <FormInputText v-if="kioskSettings.editing" v-model="kioskSettings.settings.receptionDashboardTitle" placeholder="Reception Dashboard Title" />
                <FormStaticLabel v-if="!kioskSettings.editing">
                  <div class="text-editor-static-display" v-html="kioskSettings.settings.receptionDashboardTitle" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Reception - Dashboard Text">
                <template v-slot:label>
                  <InlineHelpPopover title="Reception - Dashboard Text" placement="right">
                    <p>The main text displayed on the main reception dashboard screen under the title.</p>
                    <p>Typically a message about how to use the system or any instructions/compliance/rules for customers.</p>
                  </InlineHelpPopover>
                </template>
                <FormTextEditor v-if="kioskSettings.editing" v-model="kioskSettings.settings.receptionDashboardText" placeholder="Reception Dashboard Text" />
                <FormStaticLabel v-if="!kioskSettings.editing">
                  <div class="text-editor-static-display" v-html="kioskSettings.settings.receptionDashboardText" />
                </FormStaticLabel>
              </FormRow>

              <FormHorizontalRule />

              <FormRow label="Pickup - Success Text">
                <template v-slot:label>
                  <InlineHelpPopover title="Pickup - Success Text" placement="right">
                    <p>The text details displayed when a pickup has been created.</p>
                    <p>This can include details about where the pickup area is located and/or what staff members to contact to continue the process.</p>
                  </InlineHelpPopover>
                </template>
                <FormTextEditor v-if="kioskSettings.editing" v-model="kioskSettings.settings.receptionPickupSuccessText" placeholder="Pickup Success Text" />
                <FormStaticLabel v-if="!kioskSettings.editing">
                  <div class="text-editor-static-display" v-html="kioskSettings.settings.receptionPickupSuccessText" />
                </FormStaticLabel>
              </FormRow>

              <FormHorizontalRule />

              <FormRow label="Admin - Pin Code">
                <template v-slot:label>
                  <InlineHelpPopover title="Admin - Pin Code" placement="right">
                    <p>Numerical pin code to access admin features / overrides on the kiosk.</p>
                    <p>If not set, the admin area is unavailable.</p>
                  </InlineHelpPopover>
                </template>
                <FormInputText type="number" v-if="kioskSettings.editing" v-model="kioskSettings.settings.adminPinCode" placeholder="Admin Pin Code" />
                <FormStaticLabel v-if="!kioskSettings.editing">
                  <div class="text-editor-static-display" v-html="kioskSettings.settings.adminPinCode" />
                </FormStaticLabel>
              </FormRow>


              <FormSubmit v-if="kioskSettings.editing" label="Update" :submitting="kioskSettings.submitting" @submitted="submitKioskSettings" @cancelled="cancelKioskSettings" />
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <!-- Print Label Settings Tab -->
      <TabPage tabid="tab-8">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" class="float-right">
              <DropdownMenuItem @click="createPrintLabelConfig" icon="fa-plus">Create Print Label Config</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Print Label Configurations</h3>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mt-2">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th>Active</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="printLabelConfig in printLabelConfigList" :key="printLabelConfig.id">
                  <td>
                    <span class="font-weight-bold">{{printLabelConfig.name}}</span>
                  </td>
                  <td><IconBoolean :value="printLabelConfig.isActive" /></td>
                  <td>{{getLookupName(PrintLabelTypeLookup, printLabelConfig.type)}}</td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editPrintLabelConfig(printLabelConfig)" icon="fa-pencil">Edit Label Config</DropdownMenuItem>
                    </ActionDropMenu>                  
                  </td>
                </tr>
              </template>
              <template v-slot:empty v-if="printLabelConfigList.length == 0">
                No print label configurations found
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <!-- Shifts Tab -->
      <TabPage tabid="tab-9">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration) && !shiftSettings.editing" class="float-right">
              <DropdownMenuItem @click="editAvailabilityShiftList" icon="fa-pencil">Edit Availability Shift List</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Availability Shift List</ContentTitle>
            <ContentSubTitle>Global shifts for production and task systems</ContentSubTitle>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mt-2">
            <FormGroup :labelColumnSize="6" :form="shiftSettings.form">
              <AvailabilityShiftList
                :editing="shiftSettings.editing"
                v-model="shiftSettings.list"
              />
              <template v-if="shiftSettings.editing">
                <FormHorizontalRule />
                <FormSubmit label="Update" :submitting="shiftSettings.submitting" @cancelled="cancelAvailabilityShifts" @submitted="submitAvailabilityShifts"></FormSubmit>
              </template>
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-10">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu class="float-right" v-if="!calendarSettings.editing">
              <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="createCalendarEvent" icon="fa-plus">Create Calendar Event</DropdownMenuItem>
              <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" @click="editCalendarHolidaySettings" icon="fa-pencil">Edit Holiday Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Calendar Events</ContentTitle>
            <ContentSubTitle>Upcoming events such as closures</ContentSubTitle>
          </div>
        </div>        

        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th><Tooltip helpStyle label="Set if the event applies to the production system">Production</Tooltip></th>
                  <th><Tooltip helpStyle label="Set if the event applies to the task system">Task</Tooltip></th>
                  <th>Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="event in calendarSettings.eventList" :key="event.id">
                  <td class="font-weight-bold">{{event.name}}</td>
                  <td>{{formatLookupName(event.type, CalendarEventTypeLookup)}}</td>
                  <td>{{formatDate(event.startDate)}}</td>
                  <td>{{formatDate(event.endDate)}}</td>
                  <td><IconBoolean :value="event.isProductionEvent" /></td>
                  <td><IconBoolean :value="event.isTaskEvent" /></td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem @click="editCalendarEvent(event)" v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.configuration)" icon="fa-pencil">Edit Event</DropdownMenuItem>
                      <DropdownMenuItem @click="deleteCalendarEvent(event)" v-if="userHasDeletePermission(PermissionTypes.AdminPermissionTypes.configuration)" icon="fa-trash-alt">Delete Event</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
        <FormHorizontalRule />

        <ContentTitle>Public Holidays</ContentTitle>
        <ContentSubTitle>Public holiday settings</ContentSubTitle>
        <div class="row">
          <div class="col-lg-6">
            <FormGroup :labelColumnSize="6" :form="calendarSettings.form">
              <FormRow label="Production Schedule Handles Public Holidays">
                <template v-slot:label>
                  <InlineHelpPopover title="Public Holidays" placement="right">
                    <p>If enabled, the production scheduler will skip public holidays when generating time and date estimates.<br />
                    Public holidays will be automatically calculated based on the account country &amp; state codes.</p>
                    <strong>Default:</strong> Enabled
                  </InlineHelpPopover>
                </template>
                <FormStaticLabel>
                  <IconBoolean v-if="!calendarSettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.productionScheduleUsePublicHolidays)" />
                  <FormCheckbox v-if="calendarSettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.productionScheduleUsePublicHolidays)" @change="setConfigSetting(AccountSettingKeys.productionScheduleUsePublicHolidays, $event)" />
                </FormStaticLabel>
              </FormRow>

              <FormRow label="Task Schedule Handles Public Holidays">
                <template v-slot:label>
                  <InlineHelpPopover title="Public Holidays" placement="right">
                    <p>If enabled, the task system will skip public holidays for scheduling and when handling reports for despatch.<br />
                    Public holidays will be automatically calculated based on the account country &amp; state codes.</p>
                    <strong>Default:</strong> Enabled
                  </InlineHelpPopover>
                </template>
                <FormStaticLabel>
                  <IconBoolean v-if="!calendarSettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.taskScheduleUsePublicHolidays)" />
                  <FormCheckbox v-if="calendarSettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.taskScheduleUsePublicHolidays)" @change="setConfigSetting(AccountSettingKeys.taskScheduleUsePublicHolidays, $event)" />
                </FormStaticLabel>
              </FormRow>
              <FormRow :label="`Upcoming public holidays for ${accountStateCode}`" v-if="!calendarSettings.editing">
                <FormStaticLabel>
                  <ul>
                  <li v-for="holiday of publicHolidayList" :key="holiday.holidayDate">{{holiday.name}} - {{formatDate(holiday.holidayDate)}}</li>
                  </ul>
                </FormStaticLabel>
              </FormRow>

              <FormSubmit v-if="calendarSettings.editing" label="Update" :submitting="calendarSettings.submitting" @submitted="submitEditingCalendarHolidaySettings" @cancelled="cancelEditingCalendarHolidaySettings" />
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-11">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu class="float-right">
              
            </ActionDropMenu>
            <ContentTitle>Label Type List</ContentTitle>
            <ContentSubTitle>Full list of all label types used throughout the system</ContentSubTitle>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Type</th>
                  <th>Labels</th>
                  <th>Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="labelType in labelTypeList" :key="labelType.key">
                  <td class="font-weight-bold">
                    {{formatLookupName(labelType.type, LinkTypeLookup) || labelType.type}}
                  </td>
                  <td>
                    {{labelType.labels.length}} Label{{displayCountPlural(labelType.labels.length)}}
                  </td>
                  <td>
                    <FormLabelTag :editable="true" :editorOnly="true" :labelType="labelType.type" :value="[]" buttonLabel="Edit" @labelUpdated="reloadLabelList" @popoverHidden="reloadLabelList" />
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import Utils from '@/utils/utils'
import form from '@/mixins/form'

import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"
import StaticDataTable from '@/components/table/staticDataTable.vue'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import AvailabilityShiftList from '@/modules/admin/components/availabilityShiftList'

import CurrencyEditDialog from '@/modules/admin/views/currencyEditDialog.vue'
import TaxRateEditDialog from '@/modules/admin/views/taxRateEditDialog.vue'
import DescriptorLookupEditDialog from '@/modules/admin/views/descriptorLookupEditDialog.vue'
import SequenceEditDialog from '@/modules/admin/views/sequenceEditDialog.vue'
import MessageTemplateEditDialog from '@/modules/admin/views/messageTemplateEditDialog.vue'
import PrintLabelConfigDialog from '@/modules/admin/views/printLabelConfigDialog.vue'
import CalendarEventEditDialog from '@/modules/admin/views/calendarEventEditDialog.vue'

import { AccountSettingKeys, DescriptorLookupTypes, DescriptorLookupTypeLookup, MessageTemplateTypeLookup, CommunicationMethodTypeLookup, PrintLabelTypeLookup, CalendarEventTypeLookup, LinkTypes, LinkTypeLookup } from '@/store/types'

const MessageTemplateInfo = [
  { name: 'Invoice', value: 'invoice', class: 'label-messagetemplate-invoice' },
  { name: 'Credit Note', value: 'creditNote', class: 'label-messagetemplate-creditnote' },
  { name: 'Quote', value: 'quote', class: 'label-messagetemplate-quote' },
  { name: 'Order', value: 'order', class: 'label-messagetemplate-order' },
  { name: 'Delivery', value: 'delivery', class: 'label-messagetemplate-delivery' },
  { name: 'Purchase Order', value: 'purchaseOrder', class: 'label-messagetemplate-purchaseorder' },
  { name: 'Customer Price List', value: 'customerPriceList', class: 'label-messagetemplate-customerpricelist' },
]

export default {
  name: "GeneralConfig",
  mixins: [form],
  components: {
    TabbedBox,
    Tab,
    TabPage,
    StaticDataTable,
    FormInputStockCode,
    AvailabilityShiftList
  },
  created() {
    this.AccountSettingKeys = AccountSettingKeys
    this.PrintLabelTypeLookup = PrintLabelTypeLookup
    this.CalendarEventTypeLookup = CalendarEventTypeLookup
    this.LinkTypeLookup = LinkTypeLookup
  },
  data: () => {
    return {
      loading: false,
      loadingError: '',
      currencyList: [],
      taxRateList: [],
      descriptorList: [],
      sequenceList: [],
      messageTemplateList: [],
      stockList: [],
      resourceGroupListLookup: [],
      printLabelConfigList: [],
      upcomingPublicHolidays: [],
      labelTypeList: [],
      accountData: {},

      accountSettings: {
        form: {},
        editing: false,
        submitting: false,
        settings: {}
      },

      reportSettings: {
        form: {},
        editing: false,
        submitting: false,
        settings: {}
      },

      kioskSettings: {
        form: {},
        editing: false,
        submitting: false,
        settings: {}
      },

      shiftSettings: {
        form: {},
        list: [],
        editing: false,
        submitting: false
      },

      calendarSettings: {
        form: {},
        eventList: [],
        editing: false,
        submitting: false,
        settings: {}
      },

      descriptorLookupOptions: DescriptorLookupTypeLookup,
      messageTemplateLookupOptions: MessageTemplateTypeLookup,
      commMethodLookupOptions: CommunicationMethodTypeLookup,

      filterOptions: [
        {
          field: 'templateType',
          name: 'Template Type',
          options: MessageTemplateInfo
        }
      ],
    };
  },
  mounted() {
    this.requestData()

    this.$apiService.adminService.getAccount().then(response => {
      this.$data.accountData = response.data
      this.$store.commit("account/setAccountData", response.data)
    })
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
      accountCountryCode: state => state.account.countryCode,
      accountStateCode: state => state.account.stateCode
    }),
    messageTemplateDataURL() {
      var params = {
      }
      return '/communication/messagetemplates?' + $.param(params)
    },
    generalStockListLookup() {
      return this.$data.stockList.map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    resourceGroupLookup() {
      return this.$data.resourceGroupListLookup.map(resourceGroup => {
        return {
          value: resourceGroup.id,
          name: resourceGroup.name
        }
      })
    },
    taxRateListLookup() {
      return this.$data.taxRateList.map(taxRate => {
        return {
          value: taxRate.id,
          name: taxRate.name
        }
      })
    },
    publicHolidayList() {
      return this.upcomingPublicHolidays.slice(0, 5)
    }
  },
  methods: {
    requestData() {
      this.loadingError = false
      this.loading = true
      Promise.all([
        this.$apiService.adminService.getCurrencyList(),
        this.$apiService.adminService.getTaxRateList(),
        this.$apiService.adminService.getDescriptorLookupList(),
        this.$apiService.adminService.getSequenceConfigList(),
        this.$apiService.adminService.getMessageTemplateList(),
        this.$apiService.adminService.getReportConfig(),
        this.$apiService.adminService.getKioskConfig(),
        this.$apiService.adminService.getPrintLabelConfigList(),
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.tasksService.getResourceGroupList(),
        this.$apiService.adminService.getAvailabilityShiftList(),
        this.$apiService.adminService.getCalendarEventList(),
        this.$apiService.adminService.getCalendarConfigSetting(),
        this.$apiService.adminService.getCalendarUpcomingPublicHolidays(),
        this.$apiService.coreService.getLabels()
      ])
      .then(results => {
        this.$data.currencyList = results[0].data
        this.$data.taxRateList = results[1].data
        this.$data.descriptorList = results[2].data
        this.$data.sequenceList = results[3].data
        this.$data.messageTemplateList = results[4].data
        this.$data.reportSettings.settings = results[5].data
        this.$data.kioskSettings.settings = results[6].data
        this.$data.printLabelConfigList = results[7].data
        this.$data.stockList = results[8].data
        this.$data.resourceGroupListLookup = results[9].data
        this.$data.shiftSettings.list = results[10].data
        this.$data.calendarSettings.eventList = results[11].data
        this.$data.calendarSettings.settings = results[12].data
        this.$data.upcomingPublicHolidays = results[13].data

        this.buildLabelTypeList(results[14].data)
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    reloadData() {
      this.requestData()
    },

    // Currency
    newCurrency() {
      this.displayModal(CurrencyEditDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Currency Added')
          this.requestData()
        }
      })
    },
    editCurrency(currency) {
      this.displayModal(CurrencyEditDialog, { 
        isCreateMode: false
      }, currency)
      .then((success) => {
        if(success) {
          this.successToast('Currency Updated')
          this.requestData()
        }
      })
    },
    deleteCurrency(currency) {
      this.confirmDeleteMessage('Delete Currency?', 'Are you sure you want to delete this currency? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.adminService.deleteCurrency(currency.id).then(response => {
            this.successToast('Currency Deleted')
            this.requestData()
          })
        }
      })
    },

    // Tax Rates
    newTaxRate() {
      this.displayModal(TaxRateEditDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Tax Rate Added')
          this.requestData()
        }
      })
    },
    editTaxRate(taxRate) {
      this.displayModal(TaxRateEditDialog, { 
        isCreateMode: false
      }, taxRate)
      .then((success) => {
        if(success) {
          this.successToast('Tax Rate Updated')
          this.requestData()
        }
      })
    },
    deleteTaxRate(taxRate) {
      this.confirmDeleteMessage('Delete Tax Rate?', 'Are you sure you want to delete this tax rate? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.adminService.deleteTaxRate(taxRate.id).then(response => {
            this.successToast('Tax Rate Deleted')
            this.requestData()
          })
        }
      })
    },

    // Descriptor Lookups
    newDescriptorLookup() {
      this.displayModal(DescriptorLookupEditDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Lookup Added')
          this.requestData()
        }
      })
    },
    editDescriptorLookup(lookup) {
      this.displayModal(DescriptorLookupEditDialog, { 
        isCreateMode: false
      }, lookup)
      .then((success) => {
        if(success) {
          this.successToast('Lookup Updated')
          this.requestData()
        }
      })
    },
    deleteDescriptorLookup(lookup) {
      this.confirmDeleteMessage('Delete Lookup?', 'Are you sure you want to delete this lookup? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.adminService.deleteDescriptorLookup(lookup.id).then(response => {
            this.successToast('Lookup Deleted')
            this.requestData()
          })
        }
      })
    },

    editSequence(sequence) {
      this.displayModal(SequenceEditDialog, {}, sequence)
      .then((success) => {
        if(success) {
          this.successToast('Sequence Updated')
          this.requestData()
        }
      })
    },

    // Display sequence number
    // Used for example
    displaySequenceNumber(prefix, padding, number) {
      return prefix + number.toString().padStart(padding, '0')
    },

    // Message Templates
    newMessageTemplate() {
      this.displayModal(MessageTemplateEditDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Template Created')
          this.requestData()
        }
      })
    },
    editMessageTemplate(template) {
      this.displayModal(MessageTemplateEditDialog, { 
        isCreateMode: false
      }, template)
      .then((success) => {
        if(success) {
          this.successToast('Template Updated')
          this.requestData()
        }
      })
    },
    deleteMessageTemplate(template) {
      this.confirmDeleteMessage('Delete Template?', 'Are you sure you want to delete this template? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.adminService.deleteMessageTemplate(template.id).then(response => {
            this.successToast('Template Deleted')
            this.requestData()
          })
        }
      })
    },

    // Report Settings
    submitReportSettings() {
      this.$data.reportSettings.submitting = true
      this.$data.reportSettings.form = this.clearFormResponse()

      this.$apiService.adminService.updateReportConfig(this.$data.reportSettings.settings)
      .then(response => {
        this.$data.reportSettings.editing = false
        this.successToast('Report Config updated')
      })
      .catch(err => {
        this.$data.reportSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.reportSettings.submitting = false
      })
    },
    cancelReportSettings() {
      this.$data.reportSettings.editing = false
      this.$apiService.adminService.getReportConfig()
      .then(response => {
        this.$data.reportSettings.settings = response.data
      })
    },

    // Kiosk Settings
    submitKioskSettings() {
      this.$data.kioskSettings.submitting = true
      this.$data.kioskSettings.form = this.clearFormResponse()

      this.$apiService.adminService.updateKioskConfig(this.$data.kioskSettings.settings)
      .then(response => {
        this.$data.kioskSettings.editing = false
        this.successToast('Kiosk Config updated')
      })
      .catch(err => {
        this.$data.kioskSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.kioskSettings.submitting = false
      })
    },
    cancelKioskSettings() {
      this.$data.kioskSettings.editing = false
      this.$apiService.adminService.getKioskConfig()
      .then(response => {
        this.$data.kioskSettings.settings = response.data
      })
    },


    // Print Label Settings
    createPrintLabelConfig() {
      this.displayModal(PrintLabelConfigDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Print Label Config Created')
          this.requestData()
        }
      })
    },
    editPrintLabelConfig(printLabelConfig) {
      this.displayModal(PrintLabelConfigDialog, { 
        isCreateMode: false
      }, printLabelConfig)
      .then((success) => {
        if(success) {
          this.successToast('Print Label Config Updated')
          this.requestData()
        }
      })
    },

    // Shift Settings
    editAvailabilityShiftList() {
      this.shiftSettings.editing = true
    },
    cancelAvailabilityShifts() {
      this.shiftSettings.editing = false

      this.$apiService.adminService.getAvailabilityShiftList()
      .then(response => {
        this.$data.shiftSettings.list = response.data
      })
    },
    submitAvailabilityShifts() {
      this.$data.shiftSettings.submitting = true
      this.$data.shiftSettings.form = this.clearFormResponse()

      let updateList = this.$data.shiftSettings.list.map(shift => {
        var outputData = _.cloneDeep(shift)
        if(shift.isNewShift)
          delete outputData.id
        return outputData
      })

      this.$apiService.adminService.updateAvailabilityShiftList({ shifts: updateList })
      .then(response => {
        this.$data.shiftSettings.editing = false
        this.successToast('Availability List Updated')
      })
      .catch(err => {
        this.$data.shiftSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.shiftSettings.submitting = false
      })
    },

    // Calendar Events
    createCalendarEvent() {
      this.displayModal(CalendarEventEditDialog, { isCreateMode: true })
      .then(success => {
        if(success) {
          this.successToast('Calendar Event Created')
          this.reloadData()
        }
      })
    },
    editCalendarEvent(event) {
      this.displayModal(CalendarEventEditDialog, {}, event)
      .then(success => {
        if(success) {
          this.successToast('Calendar Event Updated')
          this.reloadData()
        }
      })
    },
    deleteCalendarEvent(event) {
      this.confirmDeleteMessage(`Delete Calendar Event?`, 'This will delete the calendar event. This action cannot be undone')
      .then(confirm => {
        if(confirm) {
          this.$apiService.adminService.deleteCalendarEvent(event.id)
          .then(response => {
            this.successToast('Calendar Event Deleted')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error deleting calendar event')
          })
        }
      })
    },
    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.calendarSettings.settings[settingKey] !== undefined)
        return this.calendarSettings.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.calendarSettings.settings[settingKey] !== undefined)
        return this.parseBoolean(this.calendarSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.calendarSettings.settings[settingKey] !== undefined)
        return parseInt(this.calendarSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.calendarSettings.settings[settingKey] !== undefined)
        return this.parseDecimal(this.calendarSettings.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.calendarSettings.settings[settingKey] = value
    },
    editCalendarHolidaySettings() {
      this.calendarSettings.editing = true
    },
    cancelEditingCalendarHolidaySettings() {
      this.calendarSettings.editing = false
      this.reloadData()
    },
    submitEditingCalendarHolidaySettings() {
      this.calendarSettings.submitting = true
      this.$apiService.adminService.updateCalendarConfigSettings({ settings: this.$data.calendarSettings.settings })
      .then(response => {
        this.$data.calendarSettings.editing = false
        this.successToast('Holiday Settings updated')
      })
      .catch(err => {
        this.$data.calendarSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.calendarSettings.submitting = false
      })
    },

    buildLabelTypeList(labelList) {
      this.labelTypeList = []

      // Add known label types incase no labels exist yet for the account (eg, new accounts)
      var availableLabelTypes = [
        LinkTypes.accountLocation,
        LinkTypes.comment,
        LinkTypes.contact,
        LinkTypes.contactAddress,
        LinkTypes.contactPerson,
        LinkTypes.contactAddressDelivery,
        LinkTypes.orderItemNCR,
        LinkTypes.productionPanelRecut,
        LinkTypes.task,
        LinkTypes.taskPartialScanIn,
        LinkTypes.taskItemLateArrival,
        LinkTypes.taskItemPartialScanOut,
        LinkTypes.taskItemStoppage,
        LinkTypes.taskItemCancellation,
        LinkTypes.creditNote,
        LinkTypes.user
      ]

      availableLabelTypes.forEach(labelType => {
        this.labelTypeList.push({ type: labelType, labels: [] })
      })

      labelList.forEach(label => {
        let curLabelTypeIndex = this.labelTypeList.findIndex(labelType => labelType.type == label.type)
        if(curLabelTypeIndex != -1) {
          this.labelTypeList[curLabelTypeIndex].labels.push(label)
          this.labelTypeList[curLabelTypeIndex].key = `${label.type}-${this.labelTypeList[curLabelTypeIndex].labels}`
        } else {
          this.labelTypeList.push({
            key: `${label.type}-1`,
            type: label.type,
            labels: [label]
          })
        }
      })

      this.$forceUpdate()
    },
    reloadLabelList() {
      this.$apiService.coreService.getLabels()
      .then(response => {
        this.buildLabelTypeList(response.data)
      })
    }
  }
}

</script>